import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import ChatArea from "../../../workspace/components/ChatArea";
import { ModelsModal } from "../../../workspace/components/ModelsModal";
import { RevoltIcon, RevoltTextarea, RevoltPopup, RevoltInput, RevoltTooltip, RevoltModal } from "../../../../components";
import "../../../workspace/components/AIChatInterface.css";

/**
 * DevChatInterface - AI chat interface for dev model testing
 * Uses ChatArea for bubbles; no app membership or workspace context.
 * Model selection and message sending is managed by parent component.
 * Supports Manage Chats (load/remove/rename) with localStorage persistence.
 */
const DevChatInterface = forwardRef(({ manager, availableModels, selectedModel, onSelectModel, onSend, onNewChat }, ref) => {
  const chatAreaRef = useRef(null);
  const editInputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showModelsModal, setShowModelsModal] = useState(false);
  const [isDragOverTextarea, setIsDragOverTextarea] = useState(false);
  const [showChatManagerPopup, setShowChatManagerPopup] = useState(false);
  const [chatManagerPopupPosition, setChatManagerPopupPosition] = useState({ top: 50, left: 50 });
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [chatList, setChatList] = useState([]);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [showVersionTooltip, setShowVersionTooltip] = useState(false);
  const [showOutdatedTooltip, setShowOutdatedTooltip] = useState(false);
  const [outdatedTooltipVersion, setOutdatedTooltipVersion] = useState(0);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [showTodosModal, setShowTodosModal] = useState(false);
  const versionTooltipRef = useRef(null);
  const outdatedTooltipAnchorRef = useRef(null);

  const refreshChatList = () => {
    if (manager?.getAllChatMetadata) {
      setChatList(manager.getAllChatMetadata());
    }
  };

  useEffect(() => {
    if (showChatManagerPopup && manager?.getAllChatMetadata) {
      setChatList(manager.getAllChatMetadata());
    }
  }, [showChatManagerPopup, manager]);

  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingChatId]);

  const calculatePopupPosition = (buttonRect) => {
    const popupWidth = 350;
    const popupHeight = 400;
    const margin = 10;

    let top = buttonRect.bottom + 5;
    let left = buttonRect.left;

    // Check if popup would go off the right edge
    if (left + popupWidth > window.innerWidth - margin) {
      left = window.innerWidth - popupWidth - margin;
    }

    // Check if popup would go off the left edge
    if (left < margin) {
      left = margin;
    }

    // Check if popup would go off the bottom edge
    if (top + popupHeight > window.innerHeight - margin) {
      top = buttonRect.top - popupHeight - 5; // Position above the button instead
    }

    // Check if popup would go off the top edge (when positioned above)
    if (top < margin) {
      top = margin;
    }

    return { top, left };
  };

  const handleSelectModel = (model) => {
    onSelectModel(model);
    setShowModelsModal(false);
  };

  const handleSend = () => {
    if (!inputValue.trim() || isGenerating || !selectedModel) {
      return;
    }
    onSend(inputValue);
    setInputValue(""); // Clear input after sending
  };

  const handleNewChatClick = () => {
    onNewChat();
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setInputValue: (value) => setInputValue(value),
    setIsGenerating: (generating) => setIsGenerating(generating),
    setInferenceError: (error) => {
      if (error != null) {
        console.error("Dev chat inference error:", error);
      }
    },
    chatAreaRef,
  }));

  return (
    <div className="ai-chat-interface">
      <div className="ai-chat-header">
        <div className="ai-chat-header-title">
          <h3>Dev Chat</h3>
          {isGenerating && (
            <div className="ai-chat-generating-indicator">
              <i className="fa-solid fa-spinner fa-spin"></i>
              <span>Generating...</span>
            </div>
          )}
        </div>
        <div className="ai-chat-header-actions">
          <button
            className="ai-chat-manage-button"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const position = calculatePopupPosition(rect);
              setChatManagerPopupPosition(position);
              setShowChatManagerPopup(true);
            }}
            disabled={isGenerating}
            title="Manage Chats"
            aria-label="Manage Chats"
          >
            <RevoltIcon name="message-square" />
          </button>
          <button
            className="ai-chat-debug-button"
            onClick={() => setShowMessagesModal(true)}
            title="View Stored Messages"
            aria-label="View Stored Messages"
          >
            <RevoltIcon name="database" />
          </button>
          <button
            className="ai-chat-debug-button"
            onClick={() => setShowTodosModal(true)}
            title="View Todo List"
            aria-label="View Todo List"
          >
            <RevoltIcon name="check-square" />
          </button>
          <button
            className="ai-chat-new-button"
            onClick={handleNewChatClick}
            title="New Chat"
            aria-label="New Chat"
          >
            <RevoltIcon name="plus" />
          </button>
        </div>
      </div>

      <div className="ai-chat-content-container">
        <ChatArea
          ref={chatAreaRef}
          onImageClick={null}
          onFileClick={null}
          onOpenFileByPath={null}
          onOpenUrl={(url) => window.open(url, "_blank")}
          onBubbleClickAction={null}
          onBubbleContextMenu={null}
          isMutationBlocked={false}
        />
      </div>

      <div className="ai-chat-footer">
        <div className="ai-chat-input-row">
          <RevoltTextarea
            className={`ai-chat-input ${isDragOverTextarea ? 'drag-over' : ''}`}
            placeholder={
              selectedModel
                ? "Ask anything..."
                : "Select a model to start chatting..."
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSubmit={handleSend}
            rows={3}
            minRows={3}
            maxRows={5}
            autoResize={true}
            disabled={!selectedModel || isGenerating}
            onDragEnter={(e) => {
              e.preventDefault();
              setIsDragOverTextarea(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragOverTextarea(false);
            }}
            onDragOver={(e) => {
              e.preventDefault(); // Allow drop
              if (!isDragOverTextarea) {
                setIsDragOverTextarea(true);
              }
            }}
            onDrop={(e) => {
              setIsDragOverTextarea(false);
              // TODO: Handle file drop
            }}
          />
          <button
            className="ai-chat-send-button"
            disabled={!selectedModel || isGenerating || !inputValue.trim()}
            title={!selectedModel ? "Select a model first" : "Send"}
            aria-label="Send"
          >
            <RevoltIcon name="send" />
          </button>
        </div>

        {/* Model selector row - in-memory only */}
        <div className="ai-chat-model-row">
          <div className="ai-chat-model-wrapper">
            <button
              className="ai-chat-model-button"
              onClick={() => setShowModelsModal(true)}
              title="Select model"
              aria-label="Select model"
            >
              <span>
                {selectedModel
                  ? selectedModel.name || selectedModel.id
                  : "Select a model..."}
              </span>
              <RevoltIcon name="chevron-down" className="model-chevron" />
            </button>
          </div>
        </div>
      </div>

      <ModelsModal
        isOpen={showModelsModal}
        onClose={() => setShowModelsModal(false)}
        onSelectModel={handleSelectModel}
      />

      {/* Manage Chats Popup */}
      <RevoltPopup
        isOpen={showChatManagerPopup}
        onClose={() => setShowChatManagerPopup(false)}
        top={chatManagerPopupPosition.top}
        left={chatManagerPopupPosition.left}
        width="350px"
        height="400px"
      >
        <div style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          color: "#ccc",
        }}>
          <div style={{
            padding: "16px",
            borderBottom: "1px solid rgba(139, 0, 0, 0.2)",
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "1.1rem" }}>Manage Chats</h3>
              <span
                ref={versionTooltipRef}
                onMouseEnter={() => setShowVersionTooltip(true)}
                onMouseLeave={() => setShowVersionTooltip(false)}
                style={{ fontSize: "0.8rem", color: "#888", cursor: "help" }}
              >
                v{manager?.chatSchemaVersion ?? 2}
              </span>
            </div>
            <RevoltTooltip
              isOpen={showVersionTooltip}
              anchorRef={versionTooltipRef}
              placement="bottom"
              maxWidth={280}
            >
              <div className="revolt-tooltip-title">Chat Version</div>
              <div className="revolt-tooltip-body">
                The current version of chats is v{manager?.chatSchemaVersion ?? 2}. Older chat formats cannot be opened.
              </div>
            </RevoltTooltip>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
              <RevoltInput
                placeholder="Search chats..."
                value={chatSearchQuery}
                onChange={(e) => setChatSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  background: "rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(139, 0, 0, 0.3)",
                  borderRadius: "4px",
                  color: "#fff",
                  padding: "6px 8px",
                  fontSize: "0.9rem",
                }}
              />
              {chatSearchQuery && (
                <button
                  onClick={() => setChatSearchQuery("")}
                  style={{
                    padding: "6px 8px",
                    background: "rgba(139, 0, 0, 0.2)",
                    border: "1px solid rgba(139, 0, 0, 0.3)",
                    borderRadius: "4px",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Clear search"
                >
                  <RevoltIcon name="x" size="sm" />
                </button>
              )}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
            {(() => {
              const filtered = chatList.filter((chat) =>
                chat.metadata.name.toLowerCase().includes(chatSearchQuery.toLowerCase())
              );
              if (filtered.length === 0) {
                return (
                  <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    No chats found
                  </div>
                );
              }
              return filtered.map((chat, index) => {
                  const now = Date.now();
                  const diffMs = now - (chat.metadata.createdAt || 0);
                  const diffSeconds = Math.floor(diffMs / 1000);
                  const diffMinutes = Math.floor(diffSeconds / 60);
                  const diffHours = Math.floor(diffMinutes / 60);
                  const isOutdated = !!chat.metadata?.isOutdated;
                  const isCurrentChat = manager?.devAIInferenceSystem?.chat?.getId?.() === chat.chatId;

                  let timeText;
                  if (diffHours >= 1) {
                    timeText = `${diffHours}h`;
                  } else if (diffMinutes >= 1) {
                    timeText = `${diffMinutes}m`;
                  } else {
                    timeText = `${Math.max(1, diffSeconds)}s`;
                  }

                  return (
                    <div
                      key={chat.chatId}
                      style={{
                        padding: "4px 20px",
                        borderBottom: index < filtered.length - 1 ? "1px solid rgba(139, 0, 0, 0.1)" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "background-color 0.15s ease",
                        opacity: isOutdated ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isOutdated) e.currentTarget.style.backgroundColor = "rgba(139, 0, 0, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          cursor: isOutdated || isGenerating ? "not-allowed" : "pointer",
                          opacity: isGenerating ? 0.6 : 1,
                          minWidth: 0,
                        }}
                        onClick={() => {
                          if (!isOutdated && !isGenerating && manager?.loadChat) {
                            manager.loadChat(chat.chatId);
                            setShowChatManagerPopup(false);
                          }
                        }}
                      >
                        {editingChatId === chat.chatId ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={async () => {
                              if (isProcessingAction || !manager?.renameChat) {
                                setEditingChatId(null);
                                setEditingName("");
                                return;
                              }
                              setIsProcessingAction(true);
                              try {
                                const success = await manager.renameChat(chat.chatId, editingName.trim() || "Untitled Chat");
                                if (success) refreshChatList();
                              } finally {
                                setIsProcessingAction(false);
                                setEditingChatId(null);
                                setEditingName("");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                              else if (e.key === "Escape") {
                                setEditingChatId(null);
                                setEditingName("");
                              }
                            }}
                            style={{
                              fontWeight: "500",
                              color: "#fff",
                              fontSize: "0.95rem",
                              background: "transparent",
                              border: "none",
                              outline: "none",
                              padding: 0,
                              margin: 0,
                              marginRight: "8px",
                              minWidth: "50px",
                              flex: 1,
                            }}
                          />
                        ) : (
                          <div style={{
                            fontWeight: "500",
                            color: "#fff",
                            fontSize: "0.95rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginRight: "8px",
                          }}>
                            {chat.metadata.name}
                          </div>
                        )}
                        <div style={{
                          fontSize: "0.75rem",
                          color: isOutdated ? "#b36b6b" : "#888",
                          whiteSpace: "nowrap",
                        }}>
                          {isOutdated ? (
                            <span
                              onMouseEnter={(e) => {
                                outdatedTooltipAnchorRef.current = e.currentTarget;
                                setOutdatedTooltipVersion(chat.metadata?.schemaVersion ?? 0);
                                setShowOutdatedTooltip(true);
                              }}
                              onMouseLeave={() => setShowOutdatedTooltip(false)}
                              style={{ cursor: "help" }}
                            >
                              Outdated v{chat.metadata?.schemaVersion ?? 0}
                            </span>
                          ) : (
                            timeText
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "12px" }}>
                        <button
                          disabled={isProcessingAction}
                          style={{
                            background: "none",
                            border: "none",
                            color: isProcessingAction ? "#666" : "#888",
                            cursor: isProcessingAction ? "not-allowed" : "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            opacity: isProcessingAction ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isProcessingAction) {
                              e.target.style.color = "#fff";
                              e.target.style.backgroundColor = "rgba(139, 0, 0, 0.2)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isProcessingAction) {
                              e.target.style.color = "#888";
                              e.target.style.backgroundColor = "transparent";
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isProcessingAction) {
                              setEditingChatId(chat.chatId);
                              setEditingName(chat.metadata.name);
                            }
                          }}
                          title="Edit chat name"
                        >
                          <RevoltIcon name="edit-2" size={16} />
                        </button>
                        <button
                          disabled={isProcessingAction || (isGenerating && isCurrentChat)}
                          style={{
                            background: "none",
                            border: "none",
                            color: isProcessingAction || (isGenerating && isCurrentChat) ? "#666" : "#888",
                            cursor: isProcessingAction || (isGenerating && isCurrentChat) ? "not-allowed" : "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            opacity: isProcessingAction || (isGenerating && isCurrentChat) ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isProcessingAction && !(isGenerating && isCurrentChat)) {
                              e.target.style.color = "#ff6b6b";
                              e.target.style.backgroundColor = "rgba(255, 107, 107, 0.1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isProcessingAction && !(isGenerating && isCurrentChat)) {
                              e.target.style.color = "#888";
                              e.target.style.backgroundColor = "transparent";
                            }
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (isProcessingAction || (isGenerating && isCurrentChat) || !manager?.deleteChat) return;
                            setIsProcessingAction(true);
                            try {
                              const success = await manager.deleteChat(chat.chatId);
                              if (success) refreshChatList();
                            } finally {
                              setIsProcessingAction(false);
                            }
                          }}
                          title="Delete chat"
                        >
                          <RevoltIcon name="trash-2" size={16} />
                        </button>
                      </div>
                    </div>
                  );
                });
            })()}
          </div>

          <RevoltTooltip
            isOpen={showOutdatedTooltip}
            anchorRef={outdatedTooltipAnchorRef}
            placement="bottom"
            maxWidth={280}
          >
            <div className="revolt-tooltip-title">Outdated Chat</div>
            <div className="revolt-tooltip-body">
              This chat was saved in format v{outdatedTooltipVersion}. The current version (v{manager?.chatSchemaVersion ?? 2}) uses a different structure, so this chat can no longer be opened.
            </div>
          </RevoltTooltip>
        </div>
      </RevoltPopup>

      {/* Messages Debug Modal */}
      <RevoltModal
        isOpen={showMessagesModal}
        onClose={() => setShowMessagesModal(false)}
        title="Stored Messages Debug"
        width="600px"
        height="500px"
      >
        <div style={{ padding: "16px", height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ margin: "0 0 8px 0", color: "#fff" }}>Messages stored in localStorage</h4>
            <p style={{ margin: 0, color: "#ccc", fontSize: "0.9rem" }}>
              These are the messages accumulated by the dev-message tool.
            </p>
          </div>

          <div style={{
            flex: 1,
            overflow: "auto",
            background: "rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(139, 0, 0, 0.3)",
            borderRadius: "8px",
            padding: "12px"
          }}>
            {(() => {
              try {
                const messages = JSON.parse(localStorage.getItem("dev-chat-messages") || "[]");
                if (messages.length === 0) {
                  return (
                    <div style={{ textAlign: "center", color: "#888", padding: "40px" }}>
                      <RevoltIcon name="database" size={32} style={{ opacity: 0.5, marginBottom: "12px" }} />
                      <p>No messages stored yet</p>
                    </div>
                  );
                }

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        style={{
                          background: "rgba(20, 20, 20, 0.8)",
                          border: "1px solid rgba(139, 0, 0, 0.2)",
                          borderRadius: "6px",
                          padding: "12px",
                          color: "#fff"
                        }}
                      >
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px"
                        }}>
                          <span style={{
                            fontSize: "0.8rem",
                            color: "#888",
                            fontWeight: "bold"
                          }}>
                            Message #{index + 1}
                          </span>
                          <span style={{
                            fontSize: "0.7rem",
                            color: "#666",
                            fontFamily: "monospace"
                          }}>
                            {new Date().toLocaleTimeString()}
                          </span>
                        </div>
                        <div style={{
                          fontSize: "0.9rem",
                          lineHeight: "1.4",
                          wordWrap: "break-word"
                        }}>
                          {message}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } catch (error) {
                return (
                  <div style={{ textAlign: "center", color: "#f87171", padding: "40px" }}>
                    <RevoltIcon name="alert-triangle" size={32} style={{ marginBottom: "12px" }} />
                    <p>Error reading messages: {error.message}</p>
                  </div>
                );
              }
            })()}
          </div>

          <div style={{
            marginTop: "16px",
            padding: "12px",
            background: "rgba(20, 20, 20, 0.5)",
            borderRadius: "6px",
            border: "1px solid rgba(139, 0, 0, 0.2)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ color: "#ccc", fontSize: "0.9rem" }}>
                Raw localStorage data:
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => {
                    const messages = JSON.parse(localStorage.getItem("dev-chat-messages") || "[]");
                    navigator.clipboard.writeText(JSON.stringify(messages, null, 2));
                  }}
                  style={{
                    background: "rgba(59, 130, 246, 0.2)",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    color: "#3b82f6",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Copy JSON
                </button>
                <button
                  onClick={() => {
                    if (window.confirm("Are you sure you want to clear all stored messages?")) {
                      localStorage.removeItem("dev-chat-messages");
                      setShowMessagesModal(false); // Close modal
                      // Optionally show a success message or refresh the modal
                    }
                  }}
                  style={{
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    color: "#ef4444",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <pre style={{
              margin: "8px 0 0 0",
              padding: "8px",
              background: "rgba(0, 0, 0, 0.5)",
              borderRadius: "4px",
              fontSize: "0.7rem",
              color: "#999",
              overflow: "auto",
              maxHeight: "100px"
            }}>
              {(() => {
                try {
                  const data = localStorage.getItem("dev-chat-messages");
                  return data ? JSON.stringify(JSON.parse(data), null, 2) : "null";
                } catch (e) {
                  return "Error parsing data";
                }
              })()}
            </pre>
          </div>
        </div>
      </RevoltModal>

      {/* Todos Debug Modal */}
      <RevoltModal
        isOpen={showTodosModal}
        onClose={() => setShowTodosModal(false)}
        title="Todo List Debug"
        containerClassName="modal-wide"
      >
        <div style={{ padding: "16px", height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ margin: "0 0 8px 0", color: "#fff" }}>Todo list for current chat</h4>
            <p style={{ margin: 0, color: "#ccc", fontSize: "0.9rem" }}>
              These are the todos managed by the todo tools for this conversation.
            </p>
          </div>

          <div style={{
            flex: 1,
            overflow: "auto",
            background: "rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(34, 197, 94, 0.3)",
            borderRadius: "8px",
            padding: "12px"
          }}>
            {(() => {
              try {
                // Get current chat ID from manager
                const chatId = manager?.currentChatId;
                if (!chatId) {
                  return (
                    <div style={{ textAlign: "center", color: "#888", padding: "40px" }}>
                      <RevoltIcon name="message-square" size={32} style={{ opacity: 0.5, marginBottom: "12px" }} />
                      <p>No active chat</p>
                    </div>
                  );
                }

                const storageKey = `dev-chat-todos-${chatId}`;
                const todos = JSON.parse(localStorage.getItem(storageKey) || "[]");

                if (todos.length === 0) {
                  return (
                    <div style={{ textAlign: "center", color: "#888", padding: "40px" }}>
                      <RevoltIcon name="check-square" size={32} style={{ opacity: 0.5, marginBottom: "12px" }} />
                      <p>No todos in this chat yet</p>
                    </div>
                  );
                }

                return (
                  <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexShrink: 0 }}>
                      <div style={{ color: "#10b981", fontSize: "0.9rem" }}>
                        ✅ Completed: {todos.filter(t => t.completed).length}
                      </div>
                      <div style={{ color: "#f59e0b", fontSize: "0.9rem" }}>
                        ⏳ Pending: {todos.filter(t => !t.completed).length}
                      </div>
                      <div style={{ color: "#6b7280", fontSize: "0.9rem" }}>
                        📊 Total: {todos.length}
                      </div>
                    </div>

                    <div style={{
                      flex: 1,
                      overflow: "auto",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
                      gap: "16px",
                      padding: "4px"
                    }}>
                      {todos.map((todo, index) => (
                      <div
                        key={todo.id || index}
                        style={{
                          background: "rgba(20, 20, 20, 0.8)",
                          border: `1px solid ${todo.completed ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                          borderRadius: "6px",
                          padding: "12px",
                          color: "#fff"
                        }}
                      >
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                              fontSize: "1.2rem",
                              color: todo.completed ? "#10b981" : "#f59e0b"
                            }}>
                              {todo.completed ? "✅" : "⬜"}
                            </span>
                            <span style={{
                              fontSize: "0.8rem",
                              color: "#888",
                              fontWeight: "bold"
                            }}>
                              Todo #{index + 1}
                            </span>
                          </div>
                          <span style={{
                            fontSize: "0.7rem",
                            color: "#666",
                            fontFamily: "monospace"
                          }}>
                            ID: {todo.id?.substring(0, 8)}...
                          </span>
                        </div>

                        <div style={{
                          fontSize: "0.95rem",
                          lineHeight: "1.4",
                          wordWrap: "break-word",
                          marginBottom: "8px",
                          textDecoration: todo.completed ? "line-through" : "none",
                          opacity: todo.completed ? 0.7 : 1
                        }}>
                          {todo.text}
                        </div>

                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.75rem",
                          color: "#888"
                        }}>
                          <span>
                            Created: {new Date(todo.createdAt).toLocaleString()}
                          </span>
                          {todo.updatedAt !== todo.createdAt && (
                            <span>
                              Updated: {new Date(todo.updatedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                      ))}
                    </div>
                  </div>
                );
              } catch (error) {
                return (
                  <div style={{ textAlign: "center", color: "#f87171", padding: "40px" }}>
                    <RevoltIcon name="alert-triangle" size={32} style={{ marginBottom: "12px" }} />
                    <p>Error reading todos: {error.message}</p>
                  </div>
                );
              }
            })()}
          </div>

          <div style={{
            marginTop: "16px",
            padding: "12px",
            background: "rgba(20, 20, 20, 0.5)",
            borderRadius: "6px",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            flexShrink: 0
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ color: "#ccc", fontSize: "0.9rem" }}>
                Raw localStorage data:
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => {
                    const chatId = manager?.currentChatId;
                    if (!chatId) return;
                    const storageKey = `dev-chat-todos-${chatId}`;
                    const todos = JSON.parse(localStorage.getItem(storageKey) || "[]");
                    navigator.clipboard.writeText(JSON.stringify(todos, null, 2));
                  }}
                  style={{
                    background: "rgba(59, 130, 246, 0.2)",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    color: "#3b82f6",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Copy JSON
                </button>
                <button
                  onClick={() => {
                    if (window.confirm("Are you sure you want to clear all todos for this chat?")) {
                      const chatId = manager?.currentChatId;
                      if (chatId) {
                        const storageKey = `dev-chat-todos-${chatId}`;
                        localStorage.removeItem(storageKey);
                        setShowTodosModal(false); // Close modal
                      }
                    }
                  }}
                  style={{
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    color: "#ef4444",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <pre style={{
              margin: "8px 0 0 0",
              padding: "8px",
              background: "rgba(0, 0, 0, 0.5)",
              borderRadius: "4px",
              fontSize: "0.7rem",
              color: "#999",
              overflow: "auto",
              maxHeight: "120px"
            }}>
              {(() => {
                try {
                  const chatId = manager?.currentChatId;
                  if (!chatId) return "No active chat";
                  const storageKey = `dev-chat-todos-${chatId}`;
                  const todos = JSON.parse(localStorage.getItem(storageKey) || "[]");
                  return JSON.stringify(todos, null, 2);
                } catch (error) {
                  return "Error parsing data";
                }
              })()}
            </pre>
          </div>
        </div>
      </RevoltModal>
    </div>
  );
});

DevChatInterface.displayName = "DevChatInterface";

export { DevChatInterface };
